import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';

const CreateTest = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    week: '',
    year: new Date().getFullYear(),
    dueDate: '',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [problems, setProblems] = useState([
    { problemNumber: 1, title: 'Problem 1', description: '', source: '', url: '' }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    setPdfFile(e.target.files[0]);
  };

  const handleProblemChange = (index, field, value) => {
    const updatedProblems = [...problems];
    updatedProblems[index][field] = value;
    setProblems(updatedProblems);
  };

  const addProblem = () => {
    setProblems([
      ...problems,
      {
        problemNumber: problems.length + 1,
        title: '',
        description: '',
        source: '',
        url: '',
      },
    ]);
  };

  const removeProblem = (index) => {
    if (problems.length > 1) {
      const updatedProblems = problems.filter((_, i) => i !== index);
      // Update problem numbers
      updatedProblems.forEach((problem, i) => {
        problem.problemNumber = i + 1;
      });
      setProblems(updatedProblems);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!formData.title || !formData.week || !formData.dueDate) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (!pdfFile) {
      setError('Please upload a PDF file');
      setLoading(false);
      return;
    }

    if (pdfFile.type !== 'application/pdf') {
      setError('Please upload a valid PDF file');
      setLoading(false);
      return;
    }

    // Validate problems
    const invalidProblems = problems.filter(p => !p.title.trim());
    if (invalidProblems.length > 0) {
      setError('Please provide titles for all problems');
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('week', formData.week);
      formDataToSend.append('year', formData.year);
      formDataToSend.append('dueDate', formData.dueDate);
      formDataToSend.append('problems', JSON.stringify(problems));
      formDataToSend.append('pdfFile', pdfFile);

      await axios.post(`${API_BASE_URL}/api/tests`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess('Test created successfully!');
      
      // Navigate to admin dashboard after 2 seconds
      setTimeout(() => {
        navigate('/admin');
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h1>Create New Test</h1>
        <p>Upload a new mock test for students to complete.</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card">
        <h2>Test Information</h2>
        
        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Test Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="form-control"
              placeholder="IMO Sprint 2026 Mock 15"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Week Number *</label>
            <input
              type="number"
              name="week"
              value={formData.week}
              onChange={handleChange}
              className="form-control"
              placeholder="15"
              min="1"
              max="52"
              required
            />
          </div>
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label className="form-label">Year</label>
            <input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleChange}
              className="form-control"
              min="2020"
              max="2030"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Due Date *</label>
            <input
              type="datetime-local"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Description (Optional)</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="form-control"
            rows="3"
            placeholder="Brief description of the test content and instructions (optional)"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Test PDF File *</label>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className="form-control"
            required
          />
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Upload the PDF containing all test questions. Max size: 10MB.
          </div>
        </div>

        <div className="card" style={{ backgroundColor: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>Problems Configuration</h3>
            <button
              type="button"
              onClick={addProblem}
              className="btn btn-primary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              Add Problem
            </button>
          </div>

          {problems.map((problem, index) => (
            <div key={index} className="card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0 }}>Problem {problem.problemNumber}</h4>
                {problems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeProblem(index)}
                    className="btn btn-danger"
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Problem Title *</label>
                  <input
                    type="text"
                    value={problem.title}
                    onChange={(e) => handleProblemChange(index, 'title', e.target.value)}
                    className="form-control"
                    placeholder="e.g., Calculus Integration"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Problem Source</label>
                  <input
                    type="text"
                    value={problem.source}
                    onChange={(e) => handleProblemChange(index, 'source', e.target.value)}
                    className="form-control"
                    placeholder="e.g., IMO 2023, USAMO 2022"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Problem URL</label>
                <input
                  type="url"
                  value={problem.url}
                  onChange={(e) => handleProblemChange(index, 'url', e.target.value)}
                  className="form-control"
                  placeholder="e.g., https://www.imo-official.org/problems/..."
                />
              </div>


              <div className="form-group">
                <label className="form-label">Problem Description (Optional)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <textarea
                    value={problem.description}
                    onChange={(e) => handleProblemChange(index, 'description', e.target.value)}
                    className="form-control"
                    rows="4"
                    placeholder="Additional notes or LaTeX — e.g. Find all $x$ such that $x^2 = 1$"
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                  />
                  <div style={{
                    padding: '10px 12px',
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    minHeight: '90px',
                    fontSize: '14px',
                    color: problem.description.trim() ? '#333' : '#999',
                  }}>
                    {problem.description.trim()
                      ? <LaTeXRenderer text={problem.description} />
                      : <em>Preview will appear here…</em>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            className="btn btn-success"
            disabled={loading}
          >
            {loading ? (
              <span>
                <div className="spinner" style={{ 
                  width: '16px', 
                  height: '16px', 
                  display: 'inline-block', 
                  marginRight: '8px' 
                }}></div>
                Creating Test...
              </span>
            ) : (
              'Create Test'
            )}
          </button>
        </div>
      </form>

      <div className="card">
        <h3>Instructions</h3>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
          <li>Ensure the PDF file contains all problem statements clearly</li>
          <li>Configure each problem with appropriate titles and scoring</li>
          <li>Set a reasonable due date for student submissions</li>
          <li>Once created, students will be able to download the PDF and submit solutions</li>
          <li>You can manage and grade submissions from the admin dashboard</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateTest;
